export default function AboutUs() {
  const milestones = [
    { year: '2018', title: 'Founded', desc: 'Started with a vision for perfection' },
    { year: '2020', title: 'Expansion', desc: 'Opened state-of-the-art facility' },
    { year: '2022', title: 'Innovation', desc: 'Launched proprietary weave technology' },
    { year: '2024', title: 'Global', desc: 'Serving 40+ countries worldwide' }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Design',
      desc: '3D scanning and CAD modeling ensure perfect OEM fitment for every component',
      icon: 'ri-pencil-ruler-2-line'
    },
    {
      number: '02',
      title: 'Engineering',
      desc: 'Aerospace-grade carbon fiber layup with optimized fiber orientation',
      icon: 'ri-settings-3-line'
    },
    {
      number: '03',
      title: 'Autoclave Curing',
      desc: 'High-pressure autoclave process creates superior strength and finish',
      icon: 'ri-fire-line'
    },
    {
      number: '04',
      title: 'Quality Control',
      desc: 'Every part undergoes rigorous inspection and fitment verification',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  return (
    <section id="about" className="bg-white relative overflow-hidden">
      {/* Full-Width Manufacturing Image */}
      <div className="relative h-[260px] sm:h-[400px] md:h-[600px] w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20high%20tech%20carbon%20fiber%20manufacturing%20facility%20with%20advanced%20autoclave%20machines%20and%20precision%20equipment%20workers%20in%20clean%20room%20suits%20inspecting%20glossy%20carbon%20fiber%20automotive%20parts%20on%20assembly%20line%20professional%20industrial%20photography%20dramatic%20lighting%20showcasing%20state%20of%20the%20art%20motorsport%20production%20environment%20wide%20angle%20view&width=1920&height=600&seq=facility-wide&orientation=landscape"
          alt="Manufacturing Facility"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent"></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 -mt-16 md:-mt-32 relative z-10">
        {/* Two Column Layout: Story + Timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 mb-16 md:mb-32 bg-white p-6 sm:p-10 md:p-12 shadow-2xl">
          {/* Left: Brand Story */}
          <div className="space-y-5 md:space-y-8">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <div className="w-8 md:w-12 h-[2px] bg-black"></div>
              <span className="text-gray-500 text-xs uppercase tracking-[3px] font-bold">OUR STORY</span>
            </div>

            <h2 className="text-black text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-[1.1]">
              ENGINEERING<br />EXCELLENCE
            </h2>

            <p className="text-gray-700 text-base md:text-lg leading-relaxed">
              Founded by motorsport enthusiasts and aerospace engineers, EagleCarbon emerged from a singular obsession: creating the world's finest carbon fiber automotive components.
            </p>

            {/* Pull Quote */}
            <div className="border-l-4 border-black pl-4 md:pl-6 py-3 md:py-4 my-4 md:my-8">
              <p className="text-black text-lg md:text-2xl font-bold leading-relaxed italic">
                "We don't just meet OEM standards—we exceed them."
              </p>
            </div>

            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              Every piece we manufacture undergoes rigorous testing in wind tunnels and on professional race circuits. Our autoclave-cured components deliver measurable performance gains while maintaining the aesthetic perfection demanded by discerning enthusiasts.
            </p>

            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              From BMW M to Porsche GT, Mercedes-AMG to Audi Sport, we've earned the trust of the world's most prestigious automotive brands and their passionate owners.
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 md:gap-6 pt-4 md:pt-8">
              <div className="border-l-4 border-black pl-3 md:pl-4">
                <div className="text-black text-3xl md:text-4xl font-black mb-1">500+</div>
                <div className="text-gray-500 text-xs uppercase tracking-wider">Unique Parts</div>
              </div>
              <div className="border-l-4 border-black pl-3 md:pl-4">
                <div className="text-black text-3xl md:text-4xl font-black mb-1">40+</div>
                <div className="text-gray-500 text-xs uppercase tracking-wider">Countries</div>
              </div>
              <div className="border-l-4 border-black pl-3 md:pl-4">
                <div className="text-black text-3xl md:text-4xl font-black mb-1">15K+</div>
                <div className="text-gray-500 text-xs uppercase tracking-wider">Builds</div>
              </div>
              <div className="border-l-4 border-black pl-3 md:pl-4">
                <div className="text-black text-3xl md:text-4xl font-black mb-1">99.8%</div>
                <div className="text-gray-500 text-xs uppercase tracking-wider">Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Right: Vertical Timeline */}
          <div className="relative">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <div className="w-8 md:w-12 h-[2px] bg-black"></div>
              <span className="text-gray-500 text-xs uppercase tracking-[3px] font-bold">OUR JOURNEY</span>
            </div>

            <div className="relative space-y-5 md:space-y-8">
              <div className="absolute left-[23px] top-0 bottom-0 w-[2px] bg-gray-200"></div>

              {milestones.map((milestone, index) => (
                <div key={index} className="relative pl-14 md:pl-16">
                  <div className="absolute left-0 top-2 w-10 h-10 md:w-12 md:h-12 bg-black rounded-full flex items-center justify-center">
                    <div className="w-5 h-5 md:w-6 md:h-6 bg-white rounded-full"></div>
                  </div>
                  <div className="bg-gray-50 p-4 md:p-6 hover:bg-gray-100 transition-colors cursor-pointer">
                    <div className="text-black text-3xl md:text-4xl font-black mb-1 md:mb-2">{milestone.year}</div>
                    <h4 className="text-black text-lg md:text-xl font-bold mb-1 md:mb-2 uppercase tracking-tight">{milestone.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{milestone.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Our Process Section */}
        <div className="pb-16 md:pb-32">
          <div className="text-center mb-10 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-4 md:mb-6">
              <div className="w-8 md:w-12 h-[2px] bg-black"></div>
              <span className="text-gray-500 text-xs uppercase tracking-[3px] font-bold">HOW WE WORK</span>
              <div className="w-8 md:w-12 h-[2px] bg-black"></div>
            </div>
            <h3 className="text-black text-3xl md:text-5xl font-black uppercase tracking-tight mb-3 md:mb-4">
              Our Process
            </h3>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
              Every EagleCarbon component follows a rigorous four-stage process to ensure unmatched quality and performance
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="group relative bg-white border-2 border-gray-200 p-6 md:p-8 hover:border-black transition-all cursor-pointer">
                <div className="absolute top-0 left-0 w-12 h-12 md:w-16 md:h-16 bg-black flex items-center justify-center">
                  <span className="text-white text-base md:text-xl font-black">{step.number}</span>
                </div>
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-4 md:mb-6 mt-6 md:mt-8">
                  <i className={`${step.icon} text-4xl md:text-5xl text-black group-hover:scale-110 transition-transform`}></i>
                </div>
                <h4 className="text-black text-xl md:text-2xl font-black mb-3 md:mb-4 uppercase tracking-tight">
                  {step.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-black group-hover:w-full transition-all duration-500"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
